<script lang="ts" setup>
import { computed, onMounted, watch } from "vue";

import PopoverOverhauling from "@Resources/js/components/popover/PopoverOverhauling.vue";
import XOverlay from "@Modules/shared/store/XOverlay";
import { XDomainsStore } from "@Modules/urls/Store/XDomainsStore";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import DomainsApi from "@Modules/teams/Api/DomainsApi";
import { Domain } from "@Modules/urls/Data/Models/DomainModel";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory";
import { UpdateUrlOptions } from "@Modules/urls/Api/UrlsApi";

import V2SingleForm from "./V2SingleForm.vue";

//
// Props
interface Props {
  urlAlias: UrlAlias;
  submit: (options: UpdateUrlOptions) => Promise<boolean>;
  errors: Record<string, string>;
  loading: boolean;
}

const props = defineProps<Props>();

//
// Setup
const overlay = XOverlay();
const domainStore = XDomainsStore();
const { currentOrganization, currentWorkspace } = useCurrentOrganization();
const domainsApi = DomainsApi();

//
// Computed
const availableTeams = computed(() => currentOrganization.value?.publicTeams() ?? []);

const filteredOrgDomains = computed<Domain[]>(() => {
  const orgDomains = domainsApi.domains.value;
  const teamSlug = currentWorkspace.value?.slug;

  return orgDomains.filter(
    (d) => d.for_all_teams || d.teams?.some(t => t.slug === teamSlug),
  );
});

const teamAvailableDomains = computed<Domain[]>(() => {
  const storeDomains = domainStore.availableDomains;
  const orgDomainNames = filteredOrgDomains.value.map((d) => d.domain);

  const combined: Domain[] = [...storeDomains];
  for (const name of orgDomainNames) {
    if (!combined.some((d) => d.domain === name)) {
      combined.push({ domain: name, isBranded: false } as Domain);
    }
  }

  return combined;
});

const selectableDomains = computed<{ value: string; name: string }[]>(() =>
  teamAvailableDomains.value.map((d) => ({ value: d.domain, name: d.domain })),
);

//
// Methods
async function fetchDomains(): Promise<void> {
  if (!currentOrganization.value?.slug) {
    return;
  }
  await domainsApi.fetchOrgDomains({ orgSlug: currentOrganization.value.slug });
}

async function handleSubmit(
  options: UpdateUrlOptions,
): Promise<boolean> {
  const result = await props.submit(options);

  // Close the edit popover only on a successful update; a failed update leaves
  // the popover open so the user can see and fix the errors.
  if (result) {
    overlay.hide();
  }

  return result;
}

//
// Watchers
watch(currentWorkspace, () => fetchDomains());

onMounted(() => fetchDomains());

//
// Exposed Test Vars
defineExpose({ domainsApi, teamAvailableDomains, selectableDomains, handleSubmit });
</script>

<template>
  <PopoverOverhauling @close="overlay.hide()">
    <template #title>
      Edit Link
    </template>

    <V2SingleForm
      v-if="currentOrganization"
      :organization="currentOrganization"
      :available-teams="availableTeams"
      :team="currentWorkspace"
      :submit="handleSubmit"
      :errors="props.errors"
      :loading="props.loading"
      :available-domains="teamAvailableDomains"
      :selectable-domains="selectableDomains"
      :url-alias="props.urlAlias"
    />
  </PopoverOverhauling>
</template>
